import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  CaretUp, CaretDown, Eye, EyeSlash, Trash, MagnifyingGlass, FloppyDisk,
  CircleNotch, Folder, FolderOpen, Plus, Camera, X,
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { MEAT_TYPE_IMAGES } from '../storefront/components/shopUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFallbackImage = (cat) => {
  if (MEAT_TYPE_IMAGES[cat.name]) return MEAT_TYPE_IMAGES[cat.name];
  const name = (cat.name || '').toLowerCase();
  for (const [key, url] of Object.entries(MEAT_TYPE_IMAGES)) {
    const k = key.toLowerCase();
    if (name.includes(k) || (name.length >= 4 && k.includes(name))) return url;
  }
  return null;
};

const CategoriesPage = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(false);
  const [uploadingCatId, setUploadingCatId] = useState(null);

  const handleImageUpload = async (catId, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image trop lourde (max 8 Mo)'); return; }
    setUploadingCatId(catId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await axios.put(`${API_URL}/api/admin/categories/${catId}`, { image_url: res.data.url }, { withCredentials: true });
      toast.success('Image de la catégorie mise à jour');
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'upload de l'image");
    } finally { setUploadingCatId(null); }
  };

  const [savingDefaults, setSavingDefaults] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const repairBrokenImages = async () => {
    if (!window.confirm("Rechercher et nettoyer les images de catégories cassées (fichiers disparus) ?\nLes catégories concernées reprendront leur image par défaut.")) return;
    setRepairing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/categories/repair-broken-images`, {}, { withCredentials: true });
      if (data.cleared === 0) toast.success('Aucune image cassée détectée ✅');
      else toast.success(`${data.cleared} image(s) cassée(s) nettoyée(s) : ${data.names.slice(0, 5).join(', ')}${data.names.length > 5 ? '…' : ''}`);
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réparation');
    } finally { setRepairing(false); }
  };

  const saveDefaultImages = async () => {
    const targets = cats.filter(c => !c.image_url && getFallbackImage(c));
    if (!targets.length) { toast.info('Aucune catégorie sans image avec un visuel par défaut disponible'); return; }
    if (!window.confirm(`Enregistrer l'image par défaut en base pour ${targets.length} catégorie(s) ? Elles deviendront modifiables comme les autres.`)) return;
    setSavingDefaults(true);
    let ok = 0, fail = 0;
    for (const c of targets) {
      try {
        await axios.put(`${API_URL}/api/admin/categories/${c.id}`, { image_url: getFallbackImage(c) }, { withCredentials: true });
        ok++;
      } catch { fail++; }
    }
    setSavingDefaults(false);
    if (fail === 0) toast.success(`${ok} image(s) par défaut enregistrée(s) en base`);
    else toast.error(`${ok} OK · ${fail} erreur(s)`);
    fetchCats();
  };

  const removeImage = async (catId) => {
    try {
      await axios.put(`${API_URL}/api/admin/categories/${catId}`, { image_url: '' }, { withCredentials: true });
      toast.success('Image retirée');
      fetchCats();
    } catch { toast.error('Erreur'); }
  };

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newCat, setNewCat] = useState({ name: '', parent_id: '' });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!newCat.name.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/categories`, {
        name: newCat.name.trim(),
        parent_id: newCat.parent_id || null,
      }, { withCredentials: true });
      toast.success('Catégorie créée');
      setNewCat({ name: '', parent_id: '' });
      setCreateOpen(false);
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de création');
    } finally { setCreating(false); }
  };

  const fetchCats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true });
      setCats(data);
    } catch (e) {
      toast.error('Erreur de chargement des catégories');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCats(); }, []);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const move = (idx, delta) => {
    setCats(prev => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setPendingOrder(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await axios.post(`${API_URL}/api/admin/categories/reorder`,
        { ordering: cats.map(c => c.id) }, { withCredentials: true });
      toast.success('Ordre enregistré');
      setPendingOrder(false);
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSavingOrder(false); }
  };

  const bulkVisibility = async (hide) => {
    if (!selected.size) return;
    try {
      await axios.post(`${API_URL}/api/admin/categories/bulk-visibility`,
        { category_ids: Array.from(selected), is_hidden: hide }, { withCredentials: true });
      toast.success(hide ? `${selected.size} catégorie(s) masquée(s)` : `${selected.size} catégorie(s) affichée(s)`);
      setSelected(new Set());
      fetchCats();
    } catch { toast.error('Erreur'); }
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Supprimer définitivement ${selected.size} catégorie(s) ? Les produits resteront mais perdront leur catégorie.`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/categories/bulk-delete`,
        { category_ids: Array.from(selected) }, { withCredentials: true });
      toast.success(`${data.deleted} catégorie(s) supprimée(s)`);
      setSelected(new Set());
      fetchCats();
    } catch { toast.error('Erreur de suppression'); }
  };

  const filtered = useMemo(() => {
    if (!search) return cats;
    const q = search.toLowerCase();
    return cats.filter(c => (c.name || '').toLowerCase().includes(q));
  }, [cats, search]);

  // Build a parent name lookup
  const nameById = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.name])), [cats]);

  // Recursive descendants check (avoid putting under self/descendants on frontend too)
  const getDescendantIds = useMemo(() => {
    const childrenOf = {};
    cats.forEach(c => {
      if (c.parent_id) {
        (childrenOf[c.parent_id] = childrenOf[c.parent_id] || []).push(c.id);
      }
    });
    return (rootId) => {
      const out = new Set();
      const walk = (id) => {
        (childrenOf[id] || []).forEach(cid => {
          if (!out.has(cid)) { out.add(cid); walk(cid); }
        });
      };
      walk(rootId);
      return out;
    };
  }, [cats]);

  const setParent = async (catId, newParentId) => {
    const payload = { parent_id: newParentId === '__root__' ? null : newParentId };
    try {
      await axios.put(`${API_URL}/api/admin/categories/${catId}`, payload, { withCredentials: true });
      toast.success(payload.parent_id ? `Déplacée sous "${nameById[payload.parent_id]}"` : 'Devenue catégorie racine');
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de déplacement');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catégories</h1>
          <p className="text-sm text-slate-500">{cats.length} catégorie(s) · {cats.filter(c => c.is_hidden).length} masquée(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={repairBrokenImages} disabled={repairing} className="gap-2" data-testid="repair-broken-images-btn" title="Nettoie les références d'images dont le fichier a disparu (ex: après un redéploiement)">
            {repairing ? <CircleNotch size={16} className="animate-spin" /> : <Trash size={16} />}
            Réparer les images cassées
          </Button>
          {cats.some(c => !c.image_url && getFallbackImage(c)) && (
            <Button variant="outline" onClick={saveDefaultImages} disabled={savingDefaults} className="gap-2" data-testid="save-default-images-btn" title="Enregistre en base les images par défaut affichées sur le site">
              {savingDefaults ? <CircleNotch size={16} className="animate-spin" /> : <Camera size={16} />}
              Enregistrer les images par défaut
            </Button>
          )}
          {pendingOrder && (
            <Button onClick={saveOrder} disabled={savingOrder} className="gap-2" data-testid="save-order-btn">
              {savingOrder ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
              Enregistrer l'ordre
            </Button>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-cat-btn">
                <Plus size={16} /> Nouvelle catégorie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer une catégorie</DialogTitle>
                <DialogDescription>Ajoutez une nouvelle catégorie au catalogue. Optionnel : choisir une catégorie parente.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Nom *</Label>
                  <Input
                    value={newCat.name}
                    onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                    placeholder="Ex: Saucisson sec"
                    autoFocus
                    data-testid="new-cat-name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Catégorie parente (optionnel)</Label>
                  <Select value={newCat.parent_id || '__root__'} onValueChange={(v) => setNewCat({ ...newCat, parent_id: v === '__root__' ? '' : v })}>
                    <SelectTrigger data-testid="new-cat-parent"><SelectValue placeholder="Aucune (racine)" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__root__">Aucune — catégorie racine</SelectItem>
                      {cats.filter(c => !c.parent_id).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreate} disabled={creating || !newCat.name.trim()} className="w-full" data-testid="save-new-cat">
                  {creating ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Rechercher une catégorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-categories"
            />
          </div>
          {selected.size > 0 && (
            <div className="flex gap-2 items-center" data-testid="bulk-actions">
              <span className="text-sm text-slate-600">{selected.size} sélectionnée(s)</span>
              <Button variant="outline" size="sm" onClick={() => bulkVisibility(true)} className="gap-1.5" data-testid="bulk-hide">
                <EyeSlash size={14} /> Masquer
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkVisibility(false)} className="gap-1.5" data-testid="bulk-show">
                <Eye size={14} /> Afficher
              </Button>
              <Button variant="destructive" size="sm" onClick={bulkDelete} className="gap-1.5" data-testid="bulk-delete">
                <Trash size={14} /> Supprimer
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><CircleNotch size={32} className="animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="grid grid-cols-[24px_40px_40px_56px_1fr_220px_120px_120px] gap-3 px-4 py-2 bg-slate-50 text-[12px] font-semibold uppercase tracking-wider text-slate-500">
              <div></div>
              <div></div>
              <div></div>
              <div>Image</div>
              <div>Nom</div>
              <div>Parent</div>
              <div>Produits</div>
              <div>Statut</div>
            </div>
            {filtered.map((c, idx) => (
              <div key={c.id} className="grid grid-cols-[24px_40px_40px_56px_1fr_220px_120px_120px] gap-3 px-4 py-3 hover:bg-slate-50 items-center" data-testid={`category-row-${c.id}`}>
                <Checkbox
                  checked={selected.has(c.id)}
                  onCheckedChange={() => toggleSelect(c.id)}
                  data-testid={`cat-cb-${c.id}`}
                />
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-up-${c.id}`}>
                  <CaretUp size={18} weight="bold" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === filtered.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-down-${c.id}`}>
                  <CaretDown size={18} weight="bold" />
                </button>
                <div className="relative group" data-testid={`cat-image-cell-${c.id}`}>
                  <label className="cursor-pointer block" title="Cliquer pour changer l'image">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => { handleImageUpload(c.id, e.target.files?.[0]); e.target.value = ''; }}
                      data-testid={`cat-image-input-${c.id}`}
                    />
                    <div className="w-11 h-11 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50 hover:ring-2 hover:ring-blue-400 transition-all relative">
                      {uploadingCatId === c.id ? (
                        <CircleNotch size={18} className="animate-spin text-blue-500" />
                      ) : c.image_url ? (
                        <img src={c.image_url.startsWith('http') ? c.image_url : `${API_URL}${c.image_url}`} alt={c.name} className="w-full h-full object-cover" />
                      ) : getFallbackImage(c) ? (
                        <>
                          <img src={getFallbackImage(c)} alt={c.name} className="w-full h-full object-cover opacity-80" title="Image par défaut utilisée sur le site — cliquez pour la remplacer" />
                          <span className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-white text-[7px] font-bold text-center leading-tight py-px" data-testid={`cat-default-badge-${c.id}`}>défaut</span>
                        </>
                      ) : (
                        <Camera size={18} className="text-slate-400" />
                      )}
                    </div>
                  </label>
                  {c.image_url && uploadingCatId !== c.id && (
                    <button
                      onClick={() => removeImage(c.id)}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white items-center justify-center hidden group-hover:flex"
                      title="Retirer l'image"
                      data-testid={`cat-image-remove-${c.id}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  {c.parent_id ? <Folder size={16} className="text-slate-400 flex-shrink-0" /> : <FolderOpen size={16} className="text-amber-500 flex-shrink-0" />}
                  <span className={`truncate ${c.is_hidden ? 'text-slate-400 italic' : 'text-slate-800'} font-medium`} data-testid={`cat-name-${c.id}`}>{c.name}</span>
                </div>
                <div className="text-[12px] text-slate-500 truncate">
                  <Select
                    value={c.parent_id || '__root__'}
                    onValueChange={(v) => setParent(c.id, v)}
                  >
                    <SelectTrigger
                      className="h-7 text-[12px] px-2 border-slate-200 hover:border-slate-300"
                      data-testid={`parent-select-${c.id}`}
                    >
                      <SelectValue placeholder="Racine" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__root__">— Racine —</SelectItem>
                      {(() => {
                        const banned = getDescendantIds(c.id);
                        banned.add(c.id);
                        return cats
                          .filter(p => !banned.has(p.id))
                          .map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.parent_id ? `↳ ${p.name}` : p.name}
                            </SelectItem>
                          ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-[13px] font-semibold text-slate-700">{c.product_count}</div>
                <div className="flex items-center gap-2">
                  {c.is_hidden ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-[10px]">Masquée</Badge>
                  ) : (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-[10px]">Visible</Badge>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/api/admin/categories/bulk-visibility`,
                          { category_ids: [c.id], is_hidden: !c.is_hidden }, { withCredentials: true });
                        toast.success(!c.is_hidden ? 'Catégorie masquée' : 'Catégorie affichée');
                        fetchCats();
                      } catch { toast.error('Erreur'); }
                    }}
                    className="text-slate-400 hover:text-slate-700"
                    title={c.is_hidden ? 'Afficher' : 'Masquer'}
                    data-testid={`toggle-vis-${c.id}`}
                  >
                    {c.is_hidden ? <EyeSlash size={16} className="text-amber-600" /> : <Eye size={16} className="text-emerald-600" />}
                  </button>
                </div>
              </div>
            ))}
            {!filtered.length && (
              <div className="p-8 text-center text-slate-400 text-sm">Aucune catégorie</div>
            )}
          </div>
        )}
      </Card>

      <p className="text-[12px] text-slate-500">
        💡 Cliquez la vignette <b>Image</b> pour changer la photo d'une catégorie (croix rouge au survol pour la retirer). Les vignettes marquées <b>« défaut »</b> montrent l'image automatique utilisée sur le site — utilisez le bouton « Enregistrer les images par défaut » pour les sauvegarder en base.<br />
        💡 Réordonnez avec les flèches ↑↓. Cliquez "Enregistrer l'ordre" pour confirmer.<br />
        💡 Changez la colonne <b>Parent</b> pour transformer une catégorie en sous-catégorie d'une autre (ou la remettre à la racine).
        Les catégories masquées sont cachées du storefront mais conservent leurs produits.
      </p>
    </div>
  );
};

export default CategoriesPage;
